import { AnimatePresence, motion } from "framer-motion";
import {
  Activity,
  BrainCircuit,
  Check,
  Code2,
  LayoutPanelTop,
  ListChecks,
  Rocket,
  Shapes,
  Sparkles,
} from "lucide-react";
import { useState } from "react";
import { FormProvider } from "react-hook-form";

import { COMPANY_SIZES, ONBOARDING_ROLES } from "./constants";
import { useOnboardingSetup } from "./hooks/useOnboardingSetup";
import type { OnboardingProductArea } from "./types";

import { Button } from "@/components/ui/button";
import { EmailInput } from "@/components/ui/EmailInput";
import { Input } from "@/components/ui/input";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { OverflowDisplayTooltip } from "@/components/ui/Tooltip/OverflowDisplayTooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

const ROLE_ICONS = {
  developer: Code2,
  prompt_engineer: Sparkles,
  founder_executive: Rocket,
  product: LayoutPanelTop,
  data_ml: BrainCircuit,
  other: Shapes,
} as const;

const PRODUCT_AREA_OPTIONS: {
  value: OnboardingProductArea;
  title: string;
  description: string;
  icon: typeof Activity;
}[] = [
  {
    value: "prompts_and_evals",
    title: "Prompts & Evaluations",
    description: "Build, version, and evaluate prompts with your team.",
    icon: ListChecks,
  },
  {
    value: "observability",
    title: "Observability",
    description: "Log requests and traces from your application.",
    icon: Activity,
  },
];

const RequiredMark = () => (
  <>
    <span className="ml-0.5 text-red-500" aria-hidden="true">
      *
    </span>
    <span className="sr-only"> required</span>
  </>
);

type CompanionState = "welcome" | "role" | "company" | "setup" | "invite";

const COMPANION_CONTENT: Record<
  CompanionState,
  { video: string; title: string; description: string }
> = {
  welcome: {
    video: "/onboarding_animations/step1_yourself.webm",
    title: "Howdy, partner.",
    description: "A little context helps us make PromptLayer feel right from the start.",
  },
  role: {
    video: "/onboarding_animations/step1_yourself.webm",
    title: "We’ll tune the workspace to you.",
    description: "Your role helps us surface the most useful tools and examples first.",
  },
  company: {
    video: "/onboarding_animations/step2_company.webm",
    title: "Built around your team.",
    description: "Team size gives us a better default for collaboration and workspace setup.",
  },
  setup: {
    video: "/onboarding_animations/step2_company.webm",
    title: "Start with what matters most.",
    description: "We’ll take you directly to the right first step after creating your workspace.",
  },
  invite: {
    video: "/onboarding_animations/step3_invite-team.webm",
    title: "Prompting is better together.",
    description: "Invite a few teammates now, or bring them in later from workspace settings.",
  },
};

const WranglerCompanion = ({ state }: { state: CompanionState }) => {
  const { video, title, description } = COMPANION_CONTENT[state];

  return (
    <aside className="sticky top-0 hidden h-fit overflow-hidden rounded-3xl border border-blue-100 bg-gradient-to-b from-blue-50/80 to-white p-5 lg:block">
      <div className="relative flex h-48 items-center justify-center overflow-hidden rounded-2xl bg-white/70">
        <div className="absolute inset-x-8 bottom-4 h-10 rounded-full bg-blue-100/60 blur-xl" />
        <AnimatePresence mode="wait">
          <motion.video
            key={state}
            src={video}
            aria-label="Wrangler onboarding animation"
            autoPlay
            loop
            muted
            playsInline
            className="relative max-h-44 max-w-52 object-contain"
            initial={{ opacity: 0, y: 8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
          />
        </AnimatePresence>
      </div>
      <AnimatePresence mode="wait">
        <motion.div
          key={`${state}-copy`}
          className="mt-4"
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.16 }}
        >
          <p className="text-sm font-semibold text-gray-950">{title}</p>
          <p className="mt-1.5 text-sm leading-6 text-gray-500">{description}</p>
        </motion.div>
      </AnimatePresence>
    </aside>
  );
};

export const Onboarding = () => {
  const { methods, onSubmit, isLoadingAnswers, isSubmitting, firstName, hasExistingWorkspace } =
    useOnboardingSetup();

  const [companionState, setCompanionState] = useState<CompanionState>("welcome");
  const selectedRole = methods.watch("you.role");
  const specificRole = methods.watch("you.specific_role");
  const companyName = methods.watch("company.company_name");
  const selectedProductAreas = methods.watch("product_areas") ?? [];
  const hasSelectedProductArea = selectedProductAreas.length > 0;
  const includesObservability = selectedProductAreas.includes("observability");
  let submitLabel = "Create Workspace & Open Starter Prompt";
  if (hasExistingWorkspace) {
    submitLabel = includesObservability ? "Continue to Observability Setup" : "Open Starter Prompt";
  } else if (includesObservability) {
    submitLabel = "Create Workspace & Set Up Observability";
  }

  const missingRequiredFields: string[] = [];
  if (!selectedRole) {
    missingRequiredFields.push("role");
  }
  if (selectedRole === "other" && !specificRole.trim()) {
    missingRequiredFields.push("specific role");
  }
  if (!companyName.trim()) {
    missingRequiredFields.push("company name");
  }

  if (!hasSelectedProductArea) {
    missingRequiredFields.push("setup area");
  }
  const hasMissingRequiredFields = missingRequiredFields.length > 0;
  const submitTooltip = `Complete the required fields to continue: ${missingRequiredFields.join(", ")}.`;
  const { errors } = methods.formState;

  if (isLoadingAnswers) {
    return (
      <div className="flex min-h-[420px] w-full items-center justify-center">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <FormProvider {...methods}>
      <form
        className="w-full max-w-5xl pb-28 pt-2 sm:pt-4"
        onSubmit={methods.handleSubmit(onSubmit)}
        noValidate
      >
        <div className="grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_260px]">
          <div className="min-w-0">
            <header className="mb-5">
              <p className="text-base font-medium text-gray-500">
                Welcome{firstName ? `, ${firstName}` : ""}
              </p>
              <h1 className="mt-1.5 text-3xl font-semibold tracking-[-0.035em] text-gray-950 sm:text-4xl">
                Let’s make PromptLayer yours
              </h1>
              <p className="mt-2 max-w-2xl text-base leading-6 text-gray-600">
                Tell us a little about you. We’ll use it to create a workspace that feels useful
                from day one.
              </p>
            </header>

            <section
              className="border-t border-gray-200 pt-4"
              onMouseEnter={() => setCompanionState("role")}
              onFocusCapture={() => setCompanionState("role")}
            >
              <div className="mb-3">
                <h2 className="text-lg font-semibold text-gray-950">
                  Your role
                  <RequiredMark />
                </h2>
                <p className="mt-1 text-sm text-gray-500">Choose the closest match.</p>
              </div>

              <div className="grid auto-rows-fr gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {ONBOARDING_ROLES.map(role => {
                  const Icon = ROLE_ICONS[role.value];
                  const isSelected = selectedRole === role.value;

                  return (
                    <button
                      key={role.value}
                      type="button"
                      aria-pressed={isSelected}
                      className={cn(
                        "group flex h-full min-h-[72px] items-center gap-3 overflow-hidden rounded-xl border px-3 py-2 text-left transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2",
                        isSelected
                          ? "border-blue-400 bg-blue-50/80 shadow-sm"
                          : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50/70",
                      )}
                      onClick={() =>
                        methods.setValue("you.role", role.value, {
                          shouldDirty: true,
                          shouldTouch: true,
                          shouldValidate: true,
                        })
                      }
                    >
                      <span
                        className={cn(
                          "flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg transition-colors",
                          isSelected
                            ? "bg-blue-100 text-blue-700"
                            : "bg-gray-100 text-gray-500 group-hover:text-gray-700",
                        )}
                      >
                        <Icon className="h-5 w-5" strokeWidth={1.8} />
                      </span>
                      <span className="min-w-0 flex-1">
                        <OverflowDisplayTooltip content={role.title}>
                          <span className="block truncate text-sm font-medium text-gray-950">
                            {role.title}
                          </span>
                        </OverflowDisplayTooltip>
                        <OverflowDisplayTooltip content={role.description}>
                          <span className="mt-0.5 block truncate text-xs text-gray-500">
                            {role.description}
                          </span>
                        </OverflowDisplayTooltip>
                      </span>
                      <span className="h-5 w-5 flex-shrink-0">
                        {isSelected && <Check className="h-5 w-5 text-blue-600" />}
                      </span>
                    </button>
                  );
                })}
              </div>

              <input
                type="hidden"
                {...methods.register("you.role", { required: "Choose the role closest to yours." })}
              />
              {errors.you?.role && (
                <p className="mt-2 text-sm text-red-600">{errors.you.role.message}</p>
              )}

              <AnimatePresence initial={false}>
                {selectedRole === "other" && (
                  <motion.div
                    className="mt-4 overflow-hidden px-0.5"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.18 }}
                  >
                    <label
                      className="mb-2 block text-sm font-medium text-gray-800"
                      htmlFor="specific-role"
                    >
                      What’s your role?
                    </label>
                    <Input
                      id="specific-role"
                      className="h-11"
                      placeholder="e.g. Designer, researcher, operations"
                      {...methods.register("you.specific_role", {
                        validate: value =>
                          selectedRole !== "other" ||
                          Boolean(value?.trim()) ||
                          "Tell us your role.",
                      })}
                    />
                    {errors.you?.specific_role && (
                      <p className="mt-2 text-sm text-red-600">
                        {errors.you.specific_role.message}
                      </p>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </section>

            <section
              className="mt-5 border-t border-gray-200 pt-4"
              onMouseEnter={() => setCompanionState("company")}
              onFocusCapture={() => setCompanionState("company")}
            >
              <div className="mb-3">
                <h2 className="text-lg font-semibold text-gray-950">Your company</h2>
                <p className="mt-1 text-sm text-gray-500">
                  Used to tailor the workspace to your team.
                </p>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label
                    className="mb-2 block text-sm font-medium text-gray-800"
                    htmlFor="company-name"
                  >
                    Company name
                    <RequiredMark />
                  </label>
                  <Input
                    id="company-name"
                    className="h-10 text-sm"
                    placeholder="Acme"
                    {...methods.register("company.company_name", {
                      required: "Enter your company name.",
                    })}
                  />
                  {errors.company?.company_name && (
                    <p className="mt-2 text-sm text-red-600">
                      {errors.company.company_name.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-800">Team size</label>
                  <Select
                    value={methods.watch("company.company_size") || undefined}
                    onValueChange={value =>
                      methods.setValue("company.company_size", value, {
                        shouldDirty: true,
                        shouldTouch: true,
                        shouldValidate: true,
                      })
                    }
                  >
                    <SelectTrigger className="h-10 text-sm">
                      <SelectValue placeholder="Select a range" />
                    </SelectTrigger>
                    <SelectContent>
                      {COMPANY_SIZES.map(size => (
                        <SelectItem key={size.value} value={size.value} className="py-2.5">
                          {size.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <input type="hidden" {...methods.register("company.company_size")} />
                </div>
              </div>
            </section>

            <section
              className="mt-5 border-t border-gray-200 pt-4"
              onMouseEnter={() => setCompanionState("setup")}
              onFocusCapture={() => setCompanionState("setup")}
            >
              <div className="mb-3">
                <h2 id="product-area-label" className="text-lg font-semibold text-gray-950">
                  What do you want to set up?
                  <RequiredMark />
                </h2>
                <p id="product-area-description" className="mt-1 text-sm text-gray-500">
                  Select all that apply. We’ll take you to the right next step.
                </p>
              </div>

              <div
                className="grid auto-rows-fr gap-2 sm:grid-cols-2"
                role="group"
                aria-labelledby="product-area-label"
                aria-describedby={
                  errors.product_areas
                    ? "product-area-description product-area-error"
                    : "product-area-description"
                }
                aria-required="true"
              >
                {PRODUCT_AREA_OPTIONS.map(option => {
                  const Icon = option.icon;
                  const isSelected = selectedProductAreas.includes(option.value);

                  return (
                    <button
                      key={option.value}
                      type="button"
                      aria-pressed={isSelected}
                      className={cn(
                        "flex h-full min-h-[76px] items-center gap-3 rounded-xl border px-3 py-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2",
                        isSelected
                          ? "border-blue-400 bg-blue-50/80"
                          : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50/70",
                      )}
                      onClick={() => {
                        const nextProductAreas = isSelected
                          ? selectedProductAreas.filter(area => area !== option.value)
                          : [...selectedProductAreas, option.value];
                        methods.setValue("product_areas", nextProductAreas, {
                          shouldDirty: true,
                          shouldTouch: true,
                          shouldValidate: true,
                        });
                      }}
                    >
                      <span
                        className={cn(
                          "flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg",
                          isSelected ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-500",
                        )}
                      >
                        <Icon className="h-5 w-5" strokeWidth={1.8} />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium text-gray-950">
                          {option.title}
                        </span>
                        <span className="mt-0.5 block text-xs leading-4 text-gray-500">
                          {option.description}
                        </span>
                      </span>
                      <span className="h-5 w-5 flex-shrink-0">
                        {isSelected && <Check className="h-5 w-5 text-blue-600" />}
                      </span>
                    </button>
                  );
                })}
              </div>

              <input
                type="hidden"
                {...methods.register("product_areas", {
                  validate: value => value.length > 0 || "Choose at least one area to get started.",
                })}
              />
              {errors.product_areas && (
                <p id="product-area-error" className="mt-2 text-sm text-red-600" role="alert">
                  {errors.product_areas.message}
                </p>
              )}
            </section>

            <section
              className="mt-5 border-t border-gray-200 pt-4"
              onMouseEnter={() => setCompanionState("invite")}
              onFocusCapture={() => setCompanionState("invite")}
            >
              <div className="mb-3">
                <h2 className="text-lg font-semibold text-gray-950">Invite your team</h2>
                <p className="mt-1 text-sm text-gray-500">
                  Optional. You can always invite teammates later.
                </p>
              </div>
              <EmailInput
                name="invitee_emails"
                label="Teammate emails"
                maxEmails={4}
                placeholder="Add email addresses"
              />
            </section>
          </div>

          <WranglerCompanion state={companionState} />
        </div>

        <div className="fixed inset-x-0 bottom-0 z-20 border-t border-gray-200 bg-white/95 shadow-[0_-4px_16px_rgba(15,23,42,0.04)] backdrop-blur-sm">
          <div className="mx-auto flex w-full max-w-5xl items-center gap-4 px-4 py-3 sm:justify-between sm:px-6">
            <p className="hidden text-sm text-gray-500 sm:block">
              {hasMissingRequiredFields
                ? "Complete the required fields to continue."
                : "You can update these details anytime."}
            </p>
            <BasicTooltip content={submitTooltip} side="top" shouldShow={hasMissingRequiredFields}>
              <span
                className="inline-flex w-full sm:w-auto"
                tabIndex={hasMissingRequiredFields ? 0 : undefined}
                aria-label={hasMissingRequiredFields ? submitTooltip : undefined}
              >
                <Button
                  type="submit"
                  size="xl"
                  isLoading={isSubmitting}
                  disabled={hasMissingRequiredFields}
                  className="h-12 w-full rounded-xl bg-blue-600 px-6 text-base font-semibold shadow-sm hover:bg-blue-700 sm:min-w-[260px]"
                >
                  {submitLabel}
                </Button>
              </span>
            </BasicTooltip>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

export default Onboarding;
